// src/views/FeatureDetailPage.js
import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, ArrowLeft } from 'lucide-react';
import { featurePagesData } from '../data/featurePagesData';

const FeatureDetailPage = () => {
  const { featureId } = useParams(); 
  const data = featurePagesData[featureId];

  // Scroll to top when page loads or feature changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  // Safety Redirect: if the URL slug doesn't exist in our data dictionary
  if (!data) {
    return <Navigate to="/features" replace />;
  }

  const APP_SIGNUP_URL = "https://app.spatchy.co/pricing";

  return (
    <div className="bg-slate-900 min-h-screen text-white pt-24 pb-32 font-sans selection:bg-blue-500 selection:text-white">
      <Helmet>
        <title>{data.title} | Spatchy Features</title>
        <meta name="description" content={data.heroSubhead} />
      </Helmet>

      {/* --- BACK NAVIGATION --- */}
      <div className="max-w-6xl mx-auto px-6 mb-8">
        <Link to="/features" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
            <ArrowLeft size={16} /> Back to all features
        </Link>
      </div>

      {/* --- 1. THE HERO SECTION --- */}
      <div className="text-center max-w-4xl mx-auto px-6 mb-24 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold uppercase mb-6">
            {data.category}
        </div>
        <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight">
          {data.heroHeadline}
        </h1>
        <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10">
          {data.heroSubhead}
        </p>
        <a 
            href={APP_SIGNUP_URL} 
            className="inline-flex px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-full transition-all shadow-lg shadow-blue-900/50 items-center gap-2"
        >
            {/* wave1-fix 7b2e9d14: real trial is 30 days, not 14 (renders on all 18 detail pages) */}
            Start 30-Day Free Trial
        </a>
      </div>

      {/* --- 2. THE 3-STEP ZIG-ZAG WALKTHROUGH --- */}
      <div className="space-y-32 mb-32 px-6">
        {data.steps && data.steps.length > 0 ? (
          data.steps.map((step, index) => (
            <div key={index} className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
                
                {/* Text Content */}
                <div className={`order-2 ${index % 2 === 0 ? 'md:order-1' : 'md:order-2'}`}>
                    <h2 className="text-3xl font-bold mb-6">{step.title}</h2>
                    <p className="text-slate-400 text-lg leading-relaxed">
                        {step.description}
                    </p>
                </div>

                {/* Visual Content (Image, Video, or Placeholder) */}
                <div className={`order-1 ${index % 2 === 0 ? 'md:order-2' : 'md:order-1'}`}>
                    <div className="bg-slate-800 rounded-2xl border border-white/10 aspect-[4/3] flex flex-col items-center justify-center shadow-2xl overflow-hidden relative group">
                        
                        {/* 1. CHECK FOR VIDEO FIRST */}
                        {step.video ? (
                            <video 
                                src={step.video} 
                                autoPlay 
                                loop 
                                muted 
                                playsInline 
                                className="w-full h-full object-contain"
                            />
                        ) : 
                        /* 2. CHECK FOR IMAGE SECOND */
                        step.image ? (
                            <img 
                                src={step.image} 
                                alt={step.title} 
                                className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105" 
                            />
                        ) : (
                            /* 3. FALLBACK TO PLACEHOLDER */
                            <div className="flex flex-col items-center justify-center p-8 text-center">
                                <div className="absolute inset-0 bg-blue-500/5 group-hover:bg-blue-500/10 transition-colors"></div>
                                <span className="text-slate-500 font-bold uppercase tracking-widest text-sm z-10">
                                    {step.imagePlaceholder || "Visual Coming Soon"}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

            </div>
          ))
        ) : (
          <div className="text-center py-20 bg-slate-800/20 rounded-3xl border border-dashed border-white/10 max-w-4xl mx-auto">
            <p className="text-slate-500 italic">Detailed walkthrough for this feature is currently being finalized.</p>
          </div>
        )}
      </div>

      {/* --- 3. THE SPATCHY DIFFERENCE --- */}
      {data.differenceHeadline && (
          <section className="px-6 mb-32">
            <div className="max-w-4xl mx-auto bg-slate-800/50 border border-blue-500/20 rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"></div>
                <CheckCircle2 className="w-12 h-12 text-blue-500 mx-auto mb-6" />
                <h3 className="text-2xl md:text-3xl font-bold mb-4">{data.differenceHeadline}</h3>
                <p className="text-slate-400 text-lg leading-relaxed max-w-2xl mx-auto">
                    {data.differenceText}
                </p>
            </div>
          </section>
      )}

      {/* --- 4. BOTTOM CTA --- */}
      <section className="text-center px-6">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Everything included. No upgrades required.</h2>
        <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Every feature on this page is included on every Spatchy plan. No upgrade tiers. No gatekeeping. Just the full toolkit from day one.
        </p>
        <a 
            href={APP_SIGNUP_URL} 
            className="inline-flex px-8 py-4 bg-white hover:bg-slate-200 text-slate-900 font-bold rounded-full transition-all shadow-lg items-center gap-2"
        >
            Get Started <ArrowRight size={18} />
        </a>
      </section>

    </div>
  );
};

export default FeatureDetailPage;